import { useSwfPlayer } from "./useSwfPlayer";
import type { SwfEvent } from "./swfTypes";
import { Loading } from "@/components/Loading/Loading";
import { ErrorState } from "@/components/ErrorState/ErrorState";

export interface SwfPlayerProps {
  swfUrl: string | null;
  autoplay?: boolean;
  onLoad?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
  onEvent?: (event: SwfEvent) => void;
}

/**
 * Kid-facing SWF player. This is the ONLY component the rest of the app
 * should import to render interactive lesson content — everything Ruffle-
 * specific lives behind useSwfPlayer/RuffleAdapter.
 */
export function SwfPlayer({
  swfUrl,
  autoplay = true,
  onComplete,
  onError,
  onEvent,
}: SwfPlayerProps) {
  const { containerRef, state, errorMessage, play, pause, enterFullscreen } =
    useSwfPlayer({
      swfUrl,
      autoplay,
      onEvent,
      onComplete,
      onError,
    });

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden rounded-2xl bg-slate-900">
      <div
        ref={containerRef}
        className="relative flex-1"
        aria-label="Lesson content"
      />

      {state === "LOADING" && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
          <Loading label="Getting your lesson ready..." />
        </div>
      )}

      {state === "ERROR" && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
          <ErrorState
            message={
              errorMessage ?? "This lesson could not be played on this device."
            }
          />
        </div>
      )}

      {(state === "PLAYING" || state === "PAUSED" || state === "LOADED") && (
        <div className="flex items-center justify-center gap-6 bg-slate-800 py-3">
          <button
            type="button"
            aria-label={state === "PLAYING" ? "Pause" : "Play"}
            onClick={state === "PLAYING" ? pause : play}
            className="grid h-14 w-14 place-items-center rounded-full bg-amber-400 text-2xl text-slate-900 active:scale-95"
          >
            {state === "PLAYING" ? "⏸" : "▶"}
          </button>
          <button
            type="button"
            aria-label="Fullscreen"
            onClick={enterFullscreen}
            className="grid h-12 w-12 place-items-center rounded-full bg-slate-700 text-xl text-white active:scale-95"
          >
            ⛶
          </button>
        </div>
      )}
    </div>
  );
}
